import React, { useEffect, useState } from 'react';
import PersonalInfo from '@mysimpledb/shared/components/Account/PersonInfo';
import useGetPersonalInfo from '@mysimpledb/shared/hooks/useGetPersonalInfo';
import useUpdatePersonalInfo from '@mysimpledb/shared/hooks/useUpdatePersonalInfo';

const PersonalInfoWeb = () => {
  const token = localStorage.getItem('token');

  const { data: rawData, loading, error, refetch } = useGetPersonalInfo(token);
  const { updateSettings: updatePersonalInfo } = useUpdatePersonalInfo(token);

  // form state
  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    phoneCountry: null,
    smsEnabled: false,
    profilePicture: '',
    allowedTypeIds: [],
    allowedSubtypeIds: [],
  });
  const [errors, setErrors] = useState({});
  const [types, setTypes] = useState([]);
  const [cacheBust, setCacheBust] = useState(0);

  // Normalize API -> form
  useEffect(() => {
    if (rawData) {
      setData({
        firstName: rawData.fname || '',
        lastName: rawData.lname || '',
        email: rawData.email || '',
        phone:
          rawData.phone || rawData.phoneNumber || rawData.phone_number || '',
        phoneCountry: rawData.phoneCountry || rawData.phone_country || null,
        smsEnabled: Boolean(rawData.smsEnabled ?? rawData.sms_enabled ?? false),
        profilePicture: rawData.profilePicture || rawData.profile_picture || '',
        allowedTypeIds:
          rawData.allowedTypeIds || rawData.allowed_type_ids || [],
        allowedSubtypeIds:
          rawData.allowedSubtypeIds || rawData.allowed_subtype_ids || [],
      });
      setTypes(rawData.types || []);
      setCacheBust(Date.now());
    }
  }, [rawData]);

  const handleFieldChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const errs = {};
    if (!data.firstName.trim()) errs.firstName = 'First name is required.';
    if (!data.lastName.trim()) errs.lastName = 'Last name is required.';
    if (!data.email.trim()) errs.email = 'Email is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Called by the child immediately after a successful upload
  const handleProfilePictureUpdate = (filename) => {
    if (!filename) return;
    setData((prev) => ({ ...prev, profilePicture: filename }));
    setCacheBust(Date.now()); // bust <img> cache
  };

  // Child calls this on Save; it may include profilePicture + type ids
  const handleSave = async ({
    profilePicture,
    phone,
    phoneCountry,
    smsEnabled,
    phoneVerificationToken,
    allowedTypeIds = [],
    allowedSubtypeIds = [],
  } = {}) => {
    if (!validate()) return;

    const payload = {
      fname: data.firstName,
      lname: data.lastName,
      email: data.email,
      phone: phone ?? data.phone,
      phoneNumber: phone ?? data.phone,
      phone_country: phoneCountry ?? data.phoneCountry,
      phoneCountry: phoneCountry ?? data.phoneCountry,
      smsEnabled: smsEnabled ?? data.smsEnabled,
      phoneVerificationToken: phoneVerificationToken || null,
      profilePicture: profilePicture ?? data.profilePicture, // <- ensure we send the new file
      typeIds: allowedTypeIds,
      subtypeIds: allowedSubtypeIds,
    };

    const result = await updatePersonalInfo(payload);
    await refetch();
    setCacheBust(Date.now());
    return result;
  };

  return (
    <PersonalInfo
      data={data}
      types={types}
      initialTypeIds={data.allowedTypeIds}
      initialSubtypeIds={data.allowedSubtypeIds}
      errors={errors}
      loading={loading}
      profilePicture={data.profilePicture}
      cacheBust={cacheBust}
      onChange={handleFieldChange}
      onSave={handleSave}
      onProfilePictureUpdate={handleProfilePictureUpdate}
    />
  );
};

export default PersonalInfoWeb;
